import {UserSettings} from "@/settings/interfaces";
import electronHelper from "@/core/services/electronHelper";

export interface StoreSchema {
    userSettings: UserSettings;
    printer?: boolean;
    groupingLogo?: string;
    isPartner?: boolean;
    token?: string;
    deviceToken?: string;
    preference?: string;
    lgoId?: string;
    apilec?: {
        version: string;
        error: number;
        reader: string;
        accessMode: number;
    };
}

export interface StorageService {
    read: (key: keyof StoreSchema | 'systemLoaded') => Promise<string | UserSettings | null>;
    store: (key: keyof StoreSchema | 'systemLoaded', value: string | UserSettings) => Promise<void>;
    destroy: (key: keyof StoreSchema | 'systemLoaded') => Promise<void>;
    mainStoreReady: (key: keyof StoreSchema | 'systemLoaded') => boolean;
}

/**
 * The storage service.
 * @author Tony Laurent <t.laurent@apodispharma.com>
 * @returns {Object} The public object.
 */
export default function (): StorageService {
    return {
        /**
         * Store the item.
         * @param {string} key - The key of the item.
         * @param {string} value - The value of the item.
         * @returns {void}
         */
        async store(key: keyof StoreSchema | 'systemLoaded', value: string | object): Promise<void> {
            if (!electronHelper.isElectron()) {
                localStorage.setItem(key, typeof value === "object" ? JSON.stringify(value) : value);

                return;
            }

            await window.ipcRenderer.invoke('store-set', key, value);
        },

        /**
         * Read the item.
         * @param {string} key - The key of the item.
         * @returns {string} The value of the item.
         */
        async read(key: keyof StoreSchema | 'systemLoaded'): Promise<string | UserSettings | null> {
            if (!electronHelper.isElectron()) {
                const value = localStorage.getItem(key as string);

                if (value) {
                    try {
                        return JSON.parse(value);
                    } catch {
                        return value;
                    }
                }

                return null;
            }

            return await window.ipcRenderer.invoke('store-get', key);
        },

        /**
         * Destroy the item.
         * @param {string} key - The key of the item.
         * @return {void}
         */
        async destroy(key: keyof StoreSchema | 'systemLoaded'): Promise<void> {
            await window.ipcRenderer?.invoke('store-delete', key);

            localStorage.removeItem(key as string);
        },

        /**
         * Check if the key should be stored in Electron store in the main process.
         * @param key
         */
        mainStoreReady(key: keyof StoreSchema | 'systemLoaded'): boolean {
            return key !== 'systemLoaded' && !!window.ipcRenderer;
        },
    };
}
